import { fromBase64, toBase64, toHex } from './atoms/convertors'
import { Hashing } from './atoms/hashing'
import { Random } from './atoms/random'
import { Symmetric, type SymmetricNonce } from './atoms/symmetric'
import { crypto_pwhash_SALTBYTES, VICTIM_IDENTITY_LENGTH } from './utils/constants'
import { getSodium, mapErr } from './utils/general'

type PdfPasswordPayload = {
  institution: string
  country: string
  countOfInstitutions: number
}

export const generatePdfPassword = async ({
  countOfInstitutions,
  country,
  institution,
}: PdfPasswordPayload) => {
  const sodium = await getSodium()

  return sodium.crypto_generichash(4, institution + country + countOfInstitutions, null, 'hex')
}

/**
 * Generates random token with sodium library.
 * @returns 64 bytes hex encoded bytes (128 chars).
 */
export const generateRandomToken = async () => toHex(await Random.generateBytes(64))

export const generateRandomInteger = Random.generateInt

export const generateRandomAlphaNumericString = async (
  length: number,
  includeCharacters: ('numbers' | 'uppercase' | 'lowercase')[] = [
    'numbers',
    'uppercase',
    'lowercase',
  ]
) => {
  const NUMBERS_ASCII_START = 48
  const UPPERCASE_ASCII_START = 65
  const LOWERCASE_ASCII_START = 97

  // gather all usable characters ascii codes into uniform array
  const characterSet = [
    ...(includeCharacters.includes('numbers')
      ? Array.from({ length: 10 }, (_, i) => i + NUMBERS_ASCII_START)
      : []),
    ...(includeCharacters.includes('uppercase')
      ? Array.from({ length: 26 }, (_, i) => i + UPPERCASE_ASCII_START)
      : []),
    ...(includeCharacters.includes('lowercase')
      ? Array.from({ length: 26 }, (_, i) => i + LOWERCASE_ASCII_START)
      : []),
  ]

  const characters = await Promise.all(
    Array.from({ length }).map(async () => {
      const index = await Random.generateInt(0, characterSet.length)

      return String.fromCharCode(characterSet[index] ?? 48)
    })
  )

  return characters.join('')
}

export const generateReportTag = async (reportTagLength: number) => {
  const bytesNeeded = Math.ceil(reportTagLength / 2)
  const hexBytes = await toHex(await Random.generateBytes(bytesNeeded))
  return hexBytes.substring(0, reportTagLength)
}

export const generateUniqueNonce = async (
  isNonceUnique: (nonce: string) => Promise<boolean>
): Promise<SymmetricNonce> => {
  const nonce = await Symmetric.generateNonce()
  const isUnique = await isNonceUnique(await toBase64(nonce))

  if (!isUnique) {
    return generateUniqueNonce(isNonceUnique)
  }

  return nonce
}

export const generateUniqueIdentity = async (
  isIdentityUnique: (identity: string) => Promise<boolean>
): Promise<string> => {
  const randomInt = await Random.generateInt(0, 999999)
  const identity = randomInt.toString().padStart(VICTIM_IDENTITY_LENGTH, '0')
  const isUnique = await isIdentityUnique(identity)
  if (!isUnique) {
    return generateUniqueIdentity(isIdentityUnique)
  }

  return identity
}

export const generateFakeSalt = async (base: string, APP_SPECIFIC_SALT: string) => {
  const sodium = await getSodium()
  const randomBaseIv = 'S3J0a3V2RG9ydDIwMjE'

  const staticSeed = new Uint8Array([104, 190, 99, 149, 68, 39, 164, 129])
  const hashSeed = await Hashing.deriveKey(
    26,
    Hashing.toPassword(base + randomBaseIv),
    Hashing.toSalt(await fromBase64(APP_SPECIFIC_SALT)),
    sodium.crypto_pwhash_MEMLIMIT_MIN * 2
  )

  if (hashSeed.isErr()) {
    return mapErr(hashSeed, 'Generate fake salt failed')
  }

  const seed = new Uint8Array(32)
  seed.set(hashSeed.value)
  seed.set(staticSeed)

  return toBase64(await Random.generateBytesDeterministic(crypto_pwhash_SALTBYTES, seed))
}
